import React, { useCallback, useEffect, useMemo } from 'react';
import logger from '@gral/log';

import url from 'url';

import { Route, Routes, useLocation, useNavigate, matchPath } from 'react-router';

import ListRoutes from '@gral/routes';

//Interface
import { IAppProps, IRoute } from './interfaces/App.interface';

//Components
import NotFound404 from '@components/Errors/404';
import Forbidden403 from '@components/Errors/403';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { routerActions } from '@redux/slices/states/router/routerSlice';
import { EAclAction } from '@ts/ui-models';

const log = logger('Router');

const Router: React.FC<IAppProps> = props => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const aclList = useAppSelector(state => state.global.aclList);
  const parsedUrl = useMemo(() => url.parse(location.pathname + location.search, true), [location.pathname, location.search]);

  const listRoutes: IRoute[] = ListRoutes;

  //#region Functions
  const checkACLForRoute = useCallback(
    (route: IRoute) => {
      if (Object.keys(aclList).length === 0) return false;
      const lastPart = route.matchPath.split('/').pop();
      const checkPermission = (entityId: string, actionId: string) => {
        return aclList[entityId]?.[actionId] ?? false;
      };

      switch (lastPart) {
        case 'view':
          return checkPermission(route.aclId, EAclAction.READ);
        case 'edit':
          return checkPermission(route.aclId, EAclAction.EDIT);
        case 'new':
          return checkPermission(route.aclId, EAclAction.CREATE);
        default:
          return checkPermission(route.aclId, EAclAction.READ);
      }
    },
    [aclList]
  );

  const memorizedRoutes = useMemo(() => {
    log.info('MemorizedRoutes aclList', aclList);
    return listRoutes.map(route => ({
      ...route,
      isAllowed: checkACLForRoute(route) || route.aclId === '',
    }));
  }, [aclList]);

  const checkRedirectRoute = useCallback(
    (route: IRoute) => {
      if (route.redirectPath) {
        const params = matchPath(route.matchPath, location.pathname)?.params;
        const redirectRoute = params?.id ? route.redirectPath.replace(':id', params.id) : route.redirectPath;
        navigate(redirectRoute);
      }
    },
    [location.pathname, navigate]
  );

  const checkBadRoute = useCallback((path: string) => {
    const prefix = '/login';
    if (path.startsWith(prefix)) {
      return path.replace(prefix, '');
    } else {
      return null;
    }
  }, []);

  //#endregion

  let route: IRoute | undefined = memorizedRoutes.find(r => matchPath(r.matchPath, location.pathname));

  const useEffectUpdateRouteHistory = (deps: React.DependencyList) => {
    useEffect(() => {
      if (route) {
        const params = matchPath(route.matchPath, location.pathname)?.params;

        if (route.redirectPath && params?.id !== 'new') {
          checkRedirectRoute(route);
          return;
        }
        if (!route.canFilter) {
          const url = new URL(window.location.href);
          url.searchParams.delete('filterID');
          window.history.pushState({}, '', url.href);
        }

        route = {
          ...route,
          fullPath: location.pathname + location.search,
        };

        if (parsedUrl.query?.filterID) {
          delete parsedUrl.query.filterID;
        }
        const browserNewTabData = Object.keys(parsedUrl.query).length > 0 ? parsedUrl.query : null;
        dispatch(routerActions.addRouteInHistoryRoute({ route, options: location.state, browserNewTabData }));
      } else {
        const checkPath = checkBadRoute(location.pathname);
        if (checkPath) {
          navigate(checkPath);
          return;
        }
      }
    }, deps);
  };

  useEffectUpdateRouteHistory([location, memorizedRoutes]);
  if (!route) return <NotFound404 />;
  return (
    <Routes>
      {memorizedRoutes.map((route, index) => (
        <Route
          key={route.matchPath + index}
          path={route.matchPath}
          element={route.isAllowed ? <route.pageComponent {...props} /> : <Forbidden403 />}
        />
      ))}
    </Routes>
  );
};

export default Router;
